import { ContentWrapper } from 'ui/atoms/ContentWrapper'
import {
  Typography,
  LazyImage,
  FormAlertLabel,
  FormContext,
} from '@frontend/design_system'
import StarSrc from 'assets/img/Star.svg'
import { PROFILE_DETAILS } from 'texts/profileDetails'
import editIcon from 'assets/icons/blackMaster/blackMaster_pencil_16x16.webp'

import { forwardRef, useContext, useState } from 'react'
import { CustomFile } from 'ui/atoms/UploadFile/types'
import styles from './styles.module.scss'
import { ContextType } from 'ui/molecules/Form/types'
import { PLEASE_UPLOAD_FILE } from 'texts/common'
import { UploadLogoModal } from './components/UploadLogoModal'
import { PlusIcon } from 'ui/icons'
import { joinClasses } from 'utils/joinClasses'

export const LogoForm = forwardRef<
  HTMLDivElement,
  {
    logo?: CustomFile[]
    logoError: boolean
    handleAddLogo: (logo: CustomFile[]) => void
    readOnly: boolean
    rating: number
    reviewCount: number
  }
>(({ logo, logoError, handleAddLogo, readOnly, rating, reviewCount }, ref) => {
  const { setDirty } = useContext<ContextType>(FormContext)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleUpdateLogo = (logo: CustomFile[]) => {
    setDirty?.(true)
    handleAddLogo(logo)
  }

  const isLogoExist = !!logo?.[0]?.url

  return (
    <ContentWrapper
      className={styles.form}
      title={PROFILE_DETAILS.UPLOAD_FILE.TITLE}
      dataTestId="upload-file-wrapper"
    >
      <div className={styles['upload-files']} ref={ref}>
        <div className={styles['upload-files-content']}>
          <div
            className={joinClasses(
              styles['upload-preview-small'],
              [styles['without-logo'], !isLogoExist],
              [styles.error, logoError]
            )}
            onClick={() => setIsModalOpen(true)}
          >
            {isLogoExist ? (
              <>
                <div className={styles['upload-preview-small-content']}>
                  <div className={styles['upload-preview-small-content-icon']}>
                    <LazyImage
                      src={editIcon}
                      alt="edit"
                      width={16}
                      height={16}
                    />
                  </div>
                </div>
                <LazyImage src={logo?.[0]?.url} alt="preview" />
              </>
            ) : (
              <PlusIcon size="large" color="grey400" />
            )}
          </div>
          {isModalOpen && (
            <UploadLogoModal
              handleUpdateLogo={handleUpdateLogo}
              onClose={() => setIsModalOpen(false)}
            />
          )}
          {!!rating && !!reviewCount && (
            <div className={styles['review-block']}>
              <div className={styles.rating}>
                <LazyImage
                  src={StarSrc}
                  className={styles['rating-icon']}
                  width={16}
                  height={16}
                />
                <Typography name="Subtitle3">{rating}</Typography>
              </div>
              <Typography name="Subtitle3" color="grey500">
                {reviewCount}{' '}
                {reviewCount === 1
                  ? PROFILE_DETAILS.REVIEW
                  : PROFILE_DETAILS.REVIEWS}
              </Typography>
            </div>
          )}
          {!readOnly && (
            <div className={styles['file-format-block']}>
              <Typography
                name="Button2"
                color="grey500"
                html={PROFILE_DETAILS.UPLOAD_FILE.SIZE}
              />
              <Typography
                name="Button2"
                color="grey500"
                html={PROFILE_DETAILS.UPLOAD_FILE.FORMAT}
              />
            </div>
          )}
        </div>
        {logoError && (
          <FormAlertLabel
            label={PLEASE_UPLOAD_FILE}
            position="static"
            typographyName="body2WMedium"
            type="error"
            dataTestId="logo-error-label"
          />
        )}
      </div>
    </ContentWrapper>
  )
})
